/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isNull from 'lodash.isnull';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  AccountLink,
  Asset,
  AssetsFilters,
  CRUDPermissions,
  Device,
  Entity,
  EntityStatus,
  GenericObject,
  GroupTypeEntity
} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {
  dateFormatter,
  timeDiff,
  toDateISOString,
  toISOString
} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {
  isValue,
  removeStringExtraSpace
} from '@ion/core/src/utils/StringUtil';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown, {
  DropDownInfiniteScrollProps
} from '@ion/dls/src/components/menus/Dropdown';
import CloseButton from '../components/buttons/CloseButton';
import FormGroupFields from '../components/forms/FormGroupFields';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import Switch from '../components/switch/Switch';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import useCloseKeyCommand from '../hooks/useCloseKeyCommand';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useFormState} from '../hooks/useFormState';
import {useMedia} from '../hooks/useMedia';
import {useMergeState} from '../hooks/useMergeState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {IonSnackbar} from '../notifications/IonSnackbar';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor, dlsZIndex} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {DLSTheme, IconSize} from '../types/types';
import CancelButton from './controls/CancelButton';
import {
  CurrentDayProps,
  DatePickerTextConfig
} from './datetimepicker/DatePicker';
import DatePicker from './datetimepicker/DatePickerExporter';
import AssetDetailView from './AssetDetailView';
import {deserialize, serialize} from './AssetEditorUtils';
import AssetList from './AssetList';
import DeviceChooser from './DeviceChooser';
import DeviceDetailView from './DeviceDetailView';
import DeviceList from './DeviceList';
import styles, {getClasses} from './editorStyles';
import EngineTypeChooser from './EngineTypeChooser';
import EntityCategoryChooser from './EntityCategoryChooser';
import EntityStatusChooser from './EntityStatusChooser';
import EntityTypeChooser from './EntityTypeChooser';
import GroupChooser from './GroupChooser';
import IOProfileChooser from './IOProfileChooser';
import MaintenanceScheduleChooser from './MaintenanceScheduleChooser';
import OdometerUnitChooser from './OdometerUnitChooser';
import ScheduleChooser from './ScheduleChooser';
import useAssetSave from './useAssetSave';

// available field names for the add/edit asset form
export enum AssetFormFields {
  Name = 'name',
  Id = 'id',
  Alias = 'externalId',
  Groups = 'groups',
  PreviousAccount = 'previousAccount',
  AssetType = 'assetType',
  AssetCategory = 'assetCategory',
  FuelType = 'fuelType',
  Status = 'status',
  Schedule = 'schedule',
  MaintenanceSchedule = 'maintenanceSchedule',
  Device = 'device',
  Devices = 'devices',
  Operator = 'operator',
  Assets = 'assets',
  IoProfile = 'ioProfile',
  InstallDate = 'installDate',
  UninstallDate = 'uninstallDate',
  Make = 'make',
  Model = 'model',
  Year = 'year',
  Vin = 'vin',
  LicenseNumber = 'licenseNumber',
  LicenseState = 'licenseState',
  OdometerReference = 'odometerReference',
  OdometerReferenceUnit = 'odometerReferenceUnit',
  OdometerReferenceTime = 'odometerReferenceTime',
  EngineHoursReference = 'engineHoursReference',
  EngineHoursReferenceDate = 'engineHoursReferenceTime',
  FuelTankCapacity = 'fuelTankCapacity',
  FuelBurnRate = 'fuelBurnRate',
  Description = 'description',
  OverrideOdometer = 'overrideOdometer',
  OverrideEngineRunTime = 'overrideEngineRunTime',
  OverrideSpeed = 'overrideSpeed',
}

export interface AssetAssociation {
  includeAssetAssociation?: boolean;
  includeFilteredAssetAssociation?: boolean;
  assetName?: string;
  assetId?: number;
}

export interface AssetEditorShowDefaultProps {
  showAddTypeDefault?: boolean;
  showAddInstallDateDefault?: boolean;
  isReadOnly?: boolean;
  onDeviceAssociated?: Function;
  isDeviceAssociated?: boolean;
}

export interface AssetTreeRefresh {
  showTreeRefresh?: boolean;
}

export interface AssetEditorProps extends AssetAssociation,
  AssetEditorShowDefaultProps,
  CurrentDayProps,
  DatePickerTextConfig,
  DropDownInfiniteScrollProps {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onSubmit?: (item: GenericObject) => void;
  onMoveDevice?: (group: Account) => void;
  onEdit?: Function;
  onAudit?: Function;
  asset?: Asset | GenericObject;
  movedToAccount?: GenericObject;
  account: Account | Account['account'];
  defaultAccount?: Account | AccountLink;
  open: boolean;
  clear?: boolean;
  loading?: boolean;
  isCtcAdmin?: boolean;
  hasEditStatusAccess?: boolean;
  hasEditDeviceAssociationAccess?: boolean;
  hasOverrideBusValueAccess?: boolean;
  isViewDialog?: boolean;
  hasDeviceMoveOption?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  includeEngineType?: boolean;
  isMultiSelectDevice?: boolean;
  assetsPermissions?: CRUDPermissions;
}

interface TargetType {
  name: AssetFormFields,
  value: unknown;
  required?: boolean
}

export const getADMAssetTypes = () => ([
  {value: 'ASSET', label: translate('admin-assets:form.options.asset')},
  {value: 'VEHICLE', label: translate('admin-assets:form.options.vehicle')},
  {value: 'TOOL', label: translate('admin-assets:form.options.tool')}
]);

const useStyles = (theme: DLSTheme) => ({
  errorMessage : {
    color      : dlsColor('red'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    marginLeft : '17px'
  },
  currentDatePicker : {
    zIndex   : dlsZIndex('dateRangePicker'),
    position : 'relative',
    padding  : toggleThemeStyle('inherit', '8px 16px !important')
  },
  activeDatePicker : {
    zIndex   : `${dlsZIndex('datePickerInputRoot')} !important`,
    position : 'relative !important'
  }
});

/**
 * @AssetEditor
 * The Asset Form with set fields and encapsulated logic for handling the form's state,
 * including validation.
 *
 * @param {boolean} open Determines if the AssetEditor dialog should be open
 * @param {object} account User's account object
 * @param {Function} onCloseDialog Callback function fired when the dialog requests to be closed
 * @param {Function} onSubmit Function the be run when the form is submitted.
 *   The function is passed an Asset object
 * @param {object} asset Asset that may be passed to the form (like in the case of editing)
 * @param {boolean} clear If true we clear the form state
 * @param {boolean} loading disables any for submission while the form is busy
 */
export const AssetEditor = ({
  open,
  onEdit,
  onAudit,
  account,
  onSubmit,
  onMoveDevice,
  onCloseDialog,
  defaultAccount,
  validDateFormats,
  validDateTimeFormats,
  onDeviceAssociated,
  assetsPermissions,
  asset = {},
  movedToAccount = {},
  isNow = false,
  clear = false,
  loading = false,
  isToday = false,
  isCtcAdmin = false,
  isViewDialog = false,
  includeEngineType = false,
  showAddTypeDefault = false,
  hasEditStatusAccess = true,
  hasEditDeviceAssociationAccess = true,
  hasOverrideBusValueAccess = false,
  hasDeviceMoveOption = false,
  allowManualDateEntry = false,
  includeGroupProperties = true,
  includeAssetAssociation = false,
  includeFilteredAssetAssociation = false,
  showAddInstallDateDefault = false,
  isReadOnly = false,
  isDeviceAssociated = false,
  isMultiSelectDevice = false,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  isInfiniteScroll = false
}: AssetEditorProps) => {
  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const editorStyles                   = useExtendStyles((theme) => styles(theme, isCurrentTheme), useStyles);
  const classes                        = editorStyles();
  const {isPhone}                      = useMedia();
  const {isVisible, onRemove, timeout} = useAnimatedMount();

  const [formState, setFormState] = useFormState({
    [AssetFormFields.Name] : {
      required : true
    },
    [AssetFormFields.Groups] : {
      required : true
    }
  });

  const [groupsLoading, setGroupsLoading]                         = useState(false);
  const [isEditMode, setIsEditMode]                               = useState(false);
  const [groupValue, setGroupValue]                               = useState(null);
  const [viewDeviceDetailed, setViewDeviceDetailed]               = useState(false);
  const [viewAssetDetailed, setViewAssetDetailed]                 = useState(false);
  const [openDatePickers, setOpenDatePickers]                     = useMergeState();
  const [deviceDetailedDoneClicked, setDeviceDetailedDoneClicked] = useState(false);
  const [deviceDetailedSelectedRow, setDeviceDetailedSelectedRow] = useState<Device | Device[]>([]);
  const [assetDetailedDoneClicked, setAssetDetailedDoneClicked]   = useState(false);
  const [assetDetailedSelectedRow, setAssetDetailedSelectedRow]   = useState<Asset[]>([]);
  const [removedDevice, setRemovedDevice]                         = useState(null);
  const [isDeviceRemoved, setIsDeviceRemoved]                     = useState(false);
  const [movingToGroup, setMovingToGroup]                         = useState(null);
  const [defaultDevice, setDefaultDevice]                         = useState<Device[]>([]);
  const [assetTypeValue, setAssetTypeValue]                       = useState<AssetsFilters['assetType']>();
  const [isValidDates, setIsValidDates]                           = useState<boolean>(true);
  const {hasUpdatePermission}                                     = assetsPermissions || {};
  const addedDevicesRef                                           = useRef([]);
  const installDate                                               = formState?.installDate?.value || null;
  const uninstallDate                                             = formState?.uninstallDate?.value || null;
  const isDeviceAssociationReadOnly                               = isViewDialog || !hasEditDeviceAssociationAccess;

  const {
    onSave,
    clearErrors,
    errorText,
    isDeviceAssociating
  } = useAssetSave({
    onSubmit,
    formState,
    handleAudit,
    removedDevice,
    onMoveDevice,
    setFormState,
    addedDevicesRef,
    isMultiSelectDevice,
    isEditMode,
    movingToGroup,
    isDeviceRemoved,
    includeAssetAssociation,
    isDeviceAssociated,
    defaultDevice
  });

  useEffect(() => {
    setIsEditMode(!isObjectEmpty(asset));
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(asset) && open) {
      setFormState(deserialize(asset));
    }
  }, [open]);

  useEffect(() => {
    setDefaultDevice(formState[AssetFormFields.Devices]?.value);
  }, [defaultDevice]);

  useEffect(() => {
    if (isCtcAdmin && !isNull(installDate) && !isNull(uninstallDate)) {
      const daysDiff = timeDiff(dateFormatter(uninstallDate), dateFormatter(installDate));
      const isValid  = daysDiff >= 0;
      setIsValidDates(isValid);
    }
  }, [installDate, uninstallDate]);

  const setDeviceDetails = () => {
    if (deviceDetailedDoneClicked) {
      const selectedDevice = deviceDetailedSelectedRow;
      const name = selectedDevice?.name;
      const value = {
        ...(selectedDevice?._self || {}),
        title : name
      };
      setFormState({devices: {value: [value], required: true, name}});
      setRemovedDevice(selectedDevice);
      setMovingToGroup(selectedDevice?.account);
    }
  };

  const setDetailsFormState = (name: AssetFormFields, selected: Array<any>, isDone: boolean) => {
    if (isDone) {
      const items = formState[name]?.value || [];
      const filteredSelected = isMultiSelectDevice ? selected?.filter(({id}) => {
        return !items?.find(({id: existingItemId}) => {
          // Compare the unique property of the selected item and existing item
          return id === existingItemId;
        });
      }) : selected;
      const value = items ? [...items, ...filteredSelected] : filteredSelected;
      setFormState({
        [name] : {
          value : !isValue(value, 'none') ? value : null,
          name
        }
      });
      if (isMultiSelectDevice) {
        addedDevicesRef.current = value?.map(device => ({
          group : device?.account,
          device
        }));
      }
    }
  };

  useEffect(() => {
    if (includeAssetAssociation) {
      setDetailsFormState(AssetFormFields.Assets, assetDetailedSelectedRow, assetDetailedDoneClicked);
    } else {
      setDeviceDetails();
    }
  }, [assetDetailedDoneClicked]);

  useEffect(() => {
    if (includeAssetAssociation) {
      setDetailsFormState(AssetFormFields.Devices, deviceDetailedSelectedRow, deviceDetailedDoneClicked);
    } else if (isMultiSelectDevice) {
      setDetailsFormState(AssetFormFields.Devices, [deviceDetailedSelectedRow], deviceDetailedDoneClicked);
    } else {
      setDeviceDetails();
    }
  }, [deviceDetailedDoneClicked]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
  };

  if (formState[AssetFormFields.Name]?.error) {
    smoothScrollByElement('editor');
  }

  const setDeviceDetailedRow = useCallback((value: Device | Device[]) => {
    setDeviceDetailedSelectedRow(value);
  }, []);

  const getDeviceDetailedDoneBtnClick = useCallback((value: boolean) => {
    setDeviceDetailedDoneClicked(value);
    if (includeAssetAssociation) {
      onDeviceAssociated?.(true);
    }
  }, []);

  const setAssetDetailedRow = useCallback((value: Asset[]) => {
    setAssetDetailedSelectedRow(value);
  }, []);

  const getAssetDetailedDoneBtnClick = useCallback((value: boolean) => {
    setAssetDetailedDoneClicked(value);
  }, []);

  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    const trimmedValue = removeStringExtraSpace(value);
    setFormState({[name]: {name, value: trimmedValue, required}});
  }, 400);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    debounceFieldChange(e.target);
  };

  function handleSwitchChange({target}) {
    const {name, checked, required = false} = target;
    setFormState({[name]: {name, value: checked, required}});
  };

  function handleChooserChange({name, value, required = false}) {
    setFormState({[name]: {name, value: isValue(value) ? null : value, required}});
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value: toISOString(value), required}});
  };

  function handleDateTimeChange({name, value, required = false}) {
    setFormState({[name]: {name, value, required}});
  };

  const handleSubmit = function(options) {
    if (isValidDates) {
      return function(e) {
        onSave(e, options);
      };
    }
  };

  function handleEdit() {
    onEdit?.();
  }

  function handleAudit() {
    onAudit?.();
  }

  const onClose = function() {
    onRemove(onCloseDialog);
  };

  const toggleDetailGrid = (show = true) => function() {
    setViewDeviceDetailed(show);
  };

  const toggleAssetDetailGrid = (show = true) => function() {
    setViewAssetDetailed(show);
  };

  const getTitle = (isEditMode: boolean, isViewDialog: boolean): string => {
    if (isViewDialog) {
      return translate('common:editors.shared.view', {entityLabel: translate('common:entityLabels.asset')});
    }
    return translate(`common:form.asset.${isEditMode ? 'edit' : 'add'}Asset`, {assetName: formState[AssetFormFields.Name].value});
  };

  useEffect(() => {
    if (isCtcAdmin) {
      setAssetTypeValue(
        formState[AssetFormFields.AssetType]?.value?.value || formState[AssetFormFields.AssetType]?.value);
    }
  }, [formState]);

  const onAssetsChange = useCallback((value: Asset[]) => {
    handleChooserChange({
      name : AssetFormFields.Assets,
      value
    });
  }, []);

  const onAssetsDialogClose = useCallback(() => {
    setViewAssetDetailed(false);
  }, []);

  const onDevicesDialogClose = useCallback(() => {
    setViewDeviceDetailed(false);
  }, []);

  const onKeyCommand = useCloseKeyCommand(onClose);

  const descriptionFormItem = (
    <FormItem fullWidth>
      <FormTextField fieldLabel={translate('common:editors.description.label')}
        disabled={isViewDialog || false}
        placeholder={translate('common:editors.description.placeholder')}
        name={AssetFormFields.Description}
        onChange={handleFieldChange}
        hasError={formState[AssetFormFields.Description]?.error}
        defaultValue={formState[AssetFormFields.Description]?.value}
        maxLength={256} />
    </FormItem>
  );

  return (
    <>
      <Dialog open={isVisible}
        transitionDuration={timeout}
        onKeyDown={isViewDialog ? onKeyCommand : null}
        classes={getClasses(classes)}>
        <DialogTitle classes={{
          root : classes.formDialogTitle
        }}>
          <div className={classes.formTitle}>
            <Render.If condition={isLegacyTheme}>
              <div onClick={onClose} className={classes.closeButton}>
                <MaterialExitCloseIcon size={IconSize.medium}
                  color={dlsColor('grey3')} />
              </div>
            </Render.If>
            <Typography component='h1' variant='h2'>
              {getTitle(isEditMode, isViewDialog)}
            </Typography>
            <Render.If condition={isCurrentTheme}>
              <div className={classes.currentCloseButton}>
                <CloseButton onClick={onClose} />
              </div>
            </Render.If>
          </div>
        </DialogTitle>
        <form className={classes.form} onSubmit={handleSubmit(false)}>
          <div id={'editor'} className={classes.formWrapper}>
            <DialogContent className={classes.formDialogContent}>
              <FormSection bottomBorder
                title={translate('common:form.asset.general')}
                legend={translate('common:editors.shared.fieldsRequired')}>
                <>
                  <FormGroupFields>
                    <FormItem fullWidth>
                      <FormTextField required
                        disabled={isViewDialog || false}
                        fieldLabel={translate('common:editors.assetName.label')}
                        placeholder={translate('common:editors.assetName.placeholder')}
                        name={AssetFormFields.Name}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Name]?.error}
                        errorText={translate('common:editors.shared.required')}
                        defaultValue={formState[AssetFormFields.Name]?.value}
                        maxLength={32} />
                    </FormItem>
                    {!isCtcAdmin && <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.assetAlias.label')}
                        disabled={isViewDialog || false}
                        placeholder={translate('common:editors.assetAlias.placeholder')}
                        name={AssetFormFields.Alias}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Alias]?.error}
                        defaultValue={formState[AssetFormFields.Alias]?.value}
                        maxLength={64} />
                    </FormItem>}
                    <FormItem>
                      <GroupChooser required
                        account={defaultAccount || account}
                        onLoad={setGroupsLoading}
                        includeGroupProperties={includeGroupProperties}
                        readOnly={isReadOnly}
                        useDisabledStyles={isReadOnly}
                        onChange={function(value) {
                          setGroupValue(value);
                          handleChooserChange({
                            name     : AssetFormFields.Groups,
                            value,
                            required : true
                          });
                        }}
                        hasError={formState[AssetFormFields.Groups]?.error}
                        defaultValue={formState[AssetFormFields.Groups]?.value || defaultAccount || account} />
                    </FormItem>
                  </FormGroupFields>
                  <FormGroupFields>
                    {!isCtcAdmin && <FormItem>
                      <EntityTypeChooser entity={GroupTypeEntity.asset}
                        isLoading={groupsLoading}
                        readOnly={isViewDialog}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.AssetType,
                            value
                          });
                        }}
                        hasError={formState[AssetFormFields.AssetType]?.error}
                        defaultValue={formState[AssetFormFields.AssetType]?.value} />
                    </FormItem>}
                    {isCtcAdmin && <FormItem>
                      <Dropdown displaySelectedLabel
                        options={getADMAssetTypes()}
                        fieldLabel={translate('common:editors.entityType.label', {name: translate('common:entityLabels.asset')})}
                        placeholder={translate('common:options.none')}
                        value={
                          formState[AssetFormFields.AssetType]?.value ||
                        (showAddTypeDefault && getADMAssetTypes()[0].value)
                        }
                        name={AssetFormFields.AssetType}
                        disabled={isViewDialog}
                        onChange={function(event, value) {
                          handleChooserChange({
                            name  : AssetFormFields.AssetType,
                            value : value
                          });
                        }} />
                    </FormItem>}
                    {!isCtcAdmin && <FormItem>
                      <EntityCategoryChooser entity={GroupTypeEntity.asset}
                        isLoading={groupsLoading}
                        readOnly={isViewDialog}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.AssetCategory,
                            value
                          });
                        }}
                        hasError={formState[AssetFormFields.AssetCategory]?.error}
                        defaultValue={formState[AssetFormFields.AssetCategory]?.value} />
                    </FormItem>}
                    {isCtcAdmin && <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.entityCategory.label', {name: translate('common:entityLabels.asset')})}
                        disabled={isViewDialog || false}
                        placeholder={translate('common:editors.entityCategory.placeholder', {name: translate('common:entityLabels.asset')})}
                        name={AssetFormFields.AssetCategory}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.AssetCategory]?.error}
                        defaultValue={formState[AssetFormFields.AssetCategory]?.value}
                        maxLength={64} />
                    </FormItem>}
                    {includeEngineType && (
                      <FormItem>
                        <EngineTypeChooser readOnly={isViewDialog}
                          onChange={function(value) {
                            handleChooserChange({
                              name : AssetFormFields.FuelType,
                              value
                            });
                          }}
                          defaultValue={formState[AssetFormFields.FuelType]?.value} />
                      </FormItem>
                    )}
                    <FormItem>
                      <EntityStatusChooser entity={Entity.asset}
                        readOnly={isViewDialog || !hasEditStatusAccess}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.Status,
                            value
                          });
                        }}
                        status={formState[AssetFormFields.Status]?.value || EntityStatus.Enabled} />
                    </FormItem>
                    {!isCtcAdmin &&<FormItem>
                      <ScheduleChooser accountId={entityId(groupValue)}
                        readOnly={isViewDialog}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.Schedule,
                            value
                          });
                        }}
                        validationError={formState[AssetFormFields.Schedule]?.error}
                        defaultValue={formState[AssetFormFields.Schedule]?.value} />
                    </FormItem>}
                    {!isCtcAdmin && <FormItem>
                      <MaintenanceScheduleChooser accountId={entityId(groupValue)}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.MaintenanceSchedule,
                            value
                          });
                        }}
                        readOnly={isViewDialog}
                        validationError={formState[AssetFormFields.MaintenanceSchedule]?.error}
                        defaultValue={formState[AssetFormFields.MaintenanceSchedule]?.value}
                        assetType={formState[AssetFormFields.AssetType]?.value}
                        assetCategory={formState[AssetFormFields.AssetCategory]?.value} />
                    </FormItem>}
                  </FormGroupFields>
                  <FormGroupFields>
                    {isCtcAdmin && descriptionFormItem}
                    <FormItem className={classes.datePickerItem}>
                      <DatePicker closeOnClickOutside
                        useDatePickerTextField
                        isToday={isToday}
                        hasError={!isValidDates}
                        manualInput={allowManualDateEntry}
                        isDatePicker={allowManualDateEntry}
                        allowManualEntry={allowManualDateEntry}
                        validDateTimeFormats={validDateFormats}
                        fieldLabel={translate('common:editors.installDate.label')}
                        placeholder={translate('common:editors.shared.selectDate')}
                        disabled={isViewDialog}
                        open={openDatePickers[AssetFormFields.InstallDate]}
                        toggle={function() {
                          setOpenDatePickers(
                            {[AssetFormFields.InstallDate]: !openDatePickers[AssetFormFields.InstallDate]}
                          );
                        }}
                        onChange={function(value, required) {
                          handleDateChange({
                            name : AssetFormFields.InstallDate,
                            value,
                            required
                          });
                        }}
                        defaultValue={
                          formState[AssetFormFields.InstallDate]?.value ||
                          (showAddInstallDateDefault && toDateISOString(new Date()))
                        } />
                    </FormItem>
                    <FormItem className={classes.datePickerItem}>
                      <DatePicker closeOnClickOutside
                        useDatePickerTextField
                        isToday={isToday}
                        hasError={!isValidDates}
                        manualInput={allowManualDateEntry}
                        isDatePicker={allowManualDateEntry}
                        allowManualEntry={allowManualDateEntry}
                        validDateTimeFormats={validDateFormats}
                        fieldLabel={translate('common:editors.uninstallDate.label')}
                        placeholder={translate('common:editors.shared.selectDate')}
                        open={openDatePickers[AssetFormFields.UninstallDate]}
                        disabled={isViewDialog}
                        toggle={function() {
                          setOpenDatePickers(
                            {[AssetFormFields.UninstallDate]: !openDatePickers[AssetFormFields.UninstallDate]}
                          );
                        }}
                        onChange={function(value, required) {
                          handleDateChange({
                            name : AssetFormFields.UninstallDate,
                            value,
                            required
                          });
                        }}
                        defaultValue={formState[AssetFormFields.UninstallDate]?.value} />
                    </FormItem>
                  </FormGroupFields>
                  {!isValidDates && (
                    <span className={classes.errorMessage}>{translate('admin-assets:invalidDateErrorMsg')}</span>
                  )}
                </>
              </FormSection>
              <FormSection bottomBorder
                title={!includeAssetAssociation
                  ? translate('common:form.asset.device')
                  : translate('groups:dialog.associations')}>
                <>
                  {includeAssetAssociation && (<>
                    <h1 className={classes.formSectionTitle}>{translate('common:form.asset.asset')}</h1>
                    <FormGroupFields>
                      <FormItem fullWidth>
                        <AssetList accountId={entityId(defaultAccount)}
                          viewGroup={isViewDialog}
                          hasDetailedViewLink={!isViewDialog}
                          isEditMode={isEditMode}
                          includeFilteredAssetAssociation={includeFilteredAssetAssociation}
                          detailedViewLinkText={translate('admin-assets:form.sections.gridSelection')}
                          onDetailedLinkClick={toggleAssetDetailGrid()}
                          validationError={formState[AssetFormFields.Assets]?.error}
                          defaultValue={formState[AssetFormFields.Assets]?.value}
                          setAssetDetailedRow={setAssetDetailedRow}
                          onChange={onAssetsChange}
                          assetId={formState[AssetFormFields.Id]?.value}
                          isInfiniteScroll={isInfiniteScroll}
                          assetTypeValue={assetTypeValue} />
                      </FormItem>
                    </FormGroupFields>
                  </>)}
                  {includeAssetAssociation && (
                    <h1 className={classes.formSectionTitle}>{translate('common:form.asset.device')}</h1>
                  )}
                  <FormGroupFields>
                    <FormItem fullWidth={isCtcAdmin}>
                      {includeAssetAssociation &&
                      <DeviceList accountId={entityId(defaultAccount)}
                        viewGroup={isViewDialog}
                        hasDetailedViewLink={!isViewDialog}
                        includeAssetAssociation={includeAssetAssociation}
                        onDeviceAssociated={onDeviceAssociated}
                        isEditMode={isEditMode}
                        isInfiniteScroll={isInfiniteScroll}
                        detailedViewLinkText={translate('admin-assets:form.sections.gridSelection')}
                        onDetailedLinkClick={toggleDetailGrid()}
                        defaultValue={formState[AssetFormFields.Devices]?.value}
                        setDeviceDetailedRow={setDeviceDetailedRow}
                        loading={groupsLoading}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.Devices,
                            value
                          });
                        }} />
                      }
                      {isMultiSelectDevice && !includeAssetAssociation &&
                      <DeviceList accountId={entityId(defaultAccount)}
                        viewGroup={isViewDialog}
                        hasDetailedViewLink={!isDeviceAssociationReadOnly}
                        includeAssetAssociation={includeAssetAssociation}
                        onDeviceAssociated={onDeviceAssociated}
                        isEditMode={isEditMode}
                        detailedViewLinkText={translate('admin-assets:form.sections.deviceDetailed')}
                        onDetailedLinkClick={toggleDetailGrid()}
                        defaultValue={formState[AssetFormFields.Devices]?.value}
                        setDeviceDetailedRow={setDeviceDetailedRow}
                        loading={groupsLoading}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.Devices,
                            value
                          });
                        }}
                        account={defaultAccount || account}
                        setRemovedDevice={setRemovedDevice}
                        setMovingToGroup={setMovingToGroup}
                        onMoveDevice={onMoveDevice}
                        validationError={formState[AssetFormFields.Devices]?.error}
                        formState={formState}
                        setFormState={setFormState}
                        readOnly={isDeviceAssociationReadOnly}
                        movedToAccount={movedToAccount}
                        hasDeviceMoveOption={hasDeviceMoveOption}
                        devicePrompt
                        isMultiDevice />
                      }
                      {!isMultiSelectDevice && !includeAssetAssociation &&
                      <DeviceChooser accountId={entityId(defaultAccount)}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.Devices,
                            value
                          });
                        }}
                        account={defaultAccount || account}
                        setRemovedDevice={setRemovedDevice}
                        setIsDeviceRemoved={setIsDeviceRemoved}
                        setMovingToGroup={setMovingToGroup}
                        hasDetailedViewLink={!isDeviceAssociationReadOnly}
                        detailedViewLinkText={translate('admin-assets:form.sections.deviceDetailed')}
                        onDetailedLinkClick={toggleDetailGrid()}
                        validationError={formState[AssetFormFields.Devices]?.error}
                        defaultValue={formState[AssetFormFields.Devices]?.value?.[0] || ''}
                        formState={formState}
                        setFormState={setFormState}
                        readOnly={isDeviceAssociationReadOnly}
                        useDisabledStyles={isDeviceAssociationReadOnly}
                        isEditMode={isEditMode}
                        movedToAccount={movedToAccount}
                        hasDeviceMoveOption={hasDeviceMoveOption}
                        devicePrompt/>
                      }
                    </FormItem>
                    {!isCtcAdmin && <FormItem>
                      <IOProfileChooser accountId={entityId(groupValue)}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.IoProfile,
                            value
                          });
                        }}
                        validationError={formState[AssetFormFields.IoProfile]?.error}
                        defaultIOProfile={formState[AssetFormFields.IoProfile]?.value} />
                    </FormItem>}
                  </FormGroupFields>
                </>
              </FormSection>
              <FormSection title={translate('common:form.asset.details')}>
                <>
                  <FormGroupFields>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.make.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.Make}
                        placeholder={translate('common:editors.make.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Make]?.error}
                        defaultValue={formState[AssetFormFields.Make]?.value}
                        maxLength={100} />
                    </FormItem>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.model.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.Model}
                        placeholder={translate('common:editors.model.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Model]?.error}
                        defaultValue={formState[AssetFormFields.Model]?.value}
                        maxLength={100} />
                    </FormItem>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.year.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.Year}
                        placeholder={translate('common:editors.year.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Year]?.error}
                        defaultValue={formState[AssetFormFields.Year]?.value}
                        maxLength={4} />
                    </FormItem>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.vin.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.Vin}
                        placeholder={translate('common:editors.vin.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.Vin]?.error}
                        defaultValue={formState[AssetFormFields.Vin]?.value}
                        maxLength={17} />
                    </FormItem>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.licenseNumber.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.LicenseNumber}
                        placeholder={translate('common:editors.licenseNumber.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.LicenseNumber]?.error}
                        defaultValue={formState[AssetFormFields.LicenseNumber]?.value}
                        maxLength={32} />
                    </FormItem>
                    {!isCtcAdmin && <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.licenseState.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.LicenseState}
                        placeholder={translate('common:editors.licenseState.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.LicenseState]?.error}
                        defaultValue={formState[AssetFormFields.LicenseState]?.value}
                        maxLength={32} />
                    </FormItem>}
                  </FormGroupFields>
                  <FormGroupFields>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.odometerReference.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.OdometerReference}
                        placeholder={translate('common:editors.odometerReference.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.OdometerReference]?.error}
                        errorText={translate('common:editors.odometerReference.errorText')}
                        defaultValue={formState[AssetFormFields.OdometerReference]?.value}
                        type='number' />
                    </FormItem>
                    <FormItem className={classnames(classes.currentDatePicker, {
                      [classes.activeDatePicker] : !!openDatePickers[AssetFormFields.OdometerReferenceTime]
                    })}>
                      <DatePicker isDateTime
                        closeOnClickOutside
                        useDatePickerTextField
                        isNow={isNow}
                        disabled={isViewDialog}
                        hideClearButton={isCtcAdmin}
                        manualInput={allowManualDateEntry}
                        isDatePicker={allowManualDateEntry}
                        allowManualEntry={allowManualDateEntry}
                        validDateTimeFormats={validDateTimeFormats}
                        fieldLabel={translate('common:editors.odometerReferenceDate.label')}
                        placeholder={translate('common:editors.shared.selectDate')}
                        open={openDatePickers[AssetFormFields.OdometerReferenceTime]}
                        toggle={function() {
                          setOpenDatePickers({
                            [AssetFormFields.OdometerReferenceTime] :
                            !openDatePickers[AssetFormFields.OdometerReferenceTime]
                          });
                        }}
                        onChange={function(value, required) {
                          handleDateTimeChange({
                            name : AssetFormFields.OdometerReferenceTime,
                            value,
                            required
                          });
                        }}
                        defaultValue={formState[AssetFormFields.OdometerReferenceTime]?.value} />
                    </FormItem>
                    <FormItem>
                      <OdometerUnitChooser hasError={formState[AssetFormFields.OdometerReferenceUnit]?.error}
                        readOnly={isViewDialog}
                        onChange={function(value) {
                          handleChooserChange({
                            name : AssetFormFields.OdometerReferenceUnit,
                            value
                          });
                        }} defaultValue={formState[AssetFormFields.OdometerReferenceUnit]?.value} />
                    </FormItem>
                  </FormGroupFields>
                  <FormGroupFields>
                    <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.engineHoursReference.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.EngineHoursReference}
                        placeholder={translate('common:editors.engineHoursReference.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.EngineHoursReference]?.error}
                        errorText={translate('common:editors.engineHoursReference.errorText')}
                        defaultValue={formState[AssetFormFields.EngineHoursReference]?.value}
                        type='number' />
                    </FormItem>
                    <FormItem className={classnames(classes.currentDatePicker, {
                      [classes.activeDatePicker] : !!openDatePickers[AssetFormFields.EngineHoursReferenceDate]
                    })}>
                      <DatePicker isDateTime
                        closeOnClickOutside
                        useDatePickerTextField
                        isNow={isNow}
                        disabled={isViewDialog}
                        hideClearButton={isCtcAdmin}
                        manualInput={allowManualDateEntry}
                        isDatePicker={allowManualDateEntry}
                        allowManualEntry={allowManualDateEntry}
                        validDateTimeFormats={validDateTimeFormats}
                        fieldLabel={translate('common:editors.engineHoursReferenceDate.label')}
                        placeholder={translate('common:editors.shared.selectDate')}
                        open={openDatePickers[AssetFormFields.EngineHoursReferenceDate]}
                        toggle={function() {
                          setOpenDatePickers({
                            [AssetFormFields.EngineHoursReferenceDate] :
                            !openDatePickers[AssetFormFields.EngineHoursReferenceDate]
                          });
                        }}
                        onChange={function(value, required) {
                          handleDateTimeChange({
                            name : AssetFormFields.EngineHoursReferenceDate,
                            value,
                            required
                          });
                        }}
                        defaultValue={formState[AssetFormFields.EngineHoursReferenceDate]?.value} />
                    </FormItem>
                    {!isCtcAdmin && <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.fuelTankCapacity.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.FuelTankCapacity}
                        placeholder={translate('common:editors.fuelTankCapacity.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.FuelTankCapacity]?.error}
                        defaultValue={formState[AssetFormFields.FuelTankCapacity]?.value}
                        type='number' />
                    </FormItem>}
                    {!isCtcAdmin && <FormItem>
                      <FormTextField fieldLabel={translate('common:editors.fuelBurnRate.label')}
                        disabled={isViewDialog || false}
                        name={AssetFormFields.FuelBurnRate}
                        placeholder={translate('common:editors.fuelBurnRate.placeholder')}
                        onChange={handleFieldChange}
                        hasError={formState[AssetFormFields.FuelBurnRate]?.error}
                        defaultValue={formState[AssetFormFields.FuelBurnRate]?.value} />
                    </FormItem>}
                    {!isCtcAdmin && descriptionFormItem}
                  </FormGroupFields>
                </>
              </FormSection>
              {!isCtcAdmin && hasOverrideBusValueAccess && <div className={classes.overrides}>
                <Typography className={classes.overridesTitle}>
                  {translate('common:form.asset.overrideBusValue')}
                </Typography>
                <FormGroupFields>
                  <div className={classes.overridesSwitches}>
                    <Switch label={translate('common:editors.odometer.label')}
                      disabled={isViewDialog}
                      marginRight={isPhone ? '25px' : '52px'}
                      name={AssetFormFields.OverrideOdometer}
                      onChange={handleSwitchChange}
                      checked={formState[AssetFormFields.OverrideOdometer]?.value} />
                    <Switch label={translate('common:editors.engineHours.label')}
                      disabled={isViewDialog}
                      marginRight={isPhone ? '25px' : '52px'}
                      name={AssetFormFields.OverrideEngineRunTime}
                      onChange={handleSwitchChange}
                      checked={formState[AssetFormFields.OverrideEngineRunTime]?.value} />
                    <Switch label={translate('common:editors.speed.label')}
                      disabled={isViewDialog}
                      marginRight={isPhone ? '25px' : '52px'}
                      name={AssetFormFields.OverrideSpeed}
                      onChange={handleSwitchChange}
                      checked={formState[AssetFormFields.OverrideSpeed]?.value} />
                  </div>
                </FormGroupFields>
              </div>}
            </DialogContent>
          </div>
        </form>
        {!isViewDialog && <DialogActions className={classes.formActions}>
          <TertiaryButton onClick={function(e) {
            clearForm();
            onClose();
          }}>
            {translate('common:editors.shared.cancel')}
          </TertiaryButton>
          <div className={classes.primaryButton}>
            <PrimaryButton fullWidth type='submit' onClick={handleSubmit(false)}>
              {
                (isDeviceAssociating || loading) ? (
                  <ButtonSpinner />
                ) : (
                  <>{translate('common:editors.shared.done')}</>
                )
              }
            </PrimaryButton>
          </div>
        </DialogActions>}
        {isViewDialog && <DialogActions className={classes.formActions}>
          {onClose && <CancelButton onCancel={onClose} />}
          <div className={classes.primaryButton}>
            <PrimaryButton onClick={handleSubmit(true)}>
              {translate('common:audit')}
            </PrimaryButton>
          </div>
          <div className={classes.primaryButton}>
            <PrimaryButton onClick={handleEdit}
              disabled={!hasUpdatePermission}>
              {translate('common:edit')}
            </PrimaryButton>
          </div>
        </DialogActions>}
      </Dialog>
      {viewDeviceDetailed && (
        <DeviceDetailView onBreadcrumbBackClick={toggleDetailGrid(false)}
          isEditMode={isEditMode}
          isCtcAdmin={isCtcAdmin}
          singleRowSelection={false}
          accountId={entityId(defaultAccount)}
          setDeviceDetailedRow={setDeviceDetailedRow}
          deviceDetailedSelectedRow={deviceDetailedSelectedRow}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          getDeviceDetailedDoneBtnClick={getDeviceDetailedDoneBtnClick}
          onCloseDialog={onDevicesDialogClose}
          usePreferedPageSize
          Features={{}}
          hideSubGroups={isCtcAdmin}
          isMultiSelectAllowed />
      )}
      {viewAssetDetailed && (
        <AssetDetailView onBreadcrumbBackClick={toggleAssetDetailGrid(false)}
          isCtcAdmin={isCtcAdmin}
          isEditMode={isEditMode}
          includeFilteredAssetAssociation={includeFilteredAssetAssociation}
          accountId={entityId(defaultAccount)}
          setAssetDetailedRow={setAssetDetailedRow}
          assetDetailedSelectedRow={assetDetailedSelectedRow}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          getAssetDetailedDoneBtnClick={getAssetDetailedDoneBtnClick}
          searchPlaceholder={translate('groups:form.sections.searchPlaceholder')}
          onCloseDialog={onAssetsDialogClose}
          assetTypeValue={assetTypeValue}
          Features={{}}
          hideSubGroups={isCtcAdmin}
          assetId={formState[AssetFormFields.Id]?.value} />
      )}
      {errorText && (
        <IonSnackbar show
          message={errorText}
          actionLabel={translate('common:ok')}
          onAction={clearErrors}
          onClose={clearErrors}
          autoHideDuration={3000} />
      )}
    </>
  );
};

export default AssetEditor;
